@file:OptIn(kotlin.uuid.ExperimentalUuidApi::class)

package com.yogaaicoach.pages.workout

import com.yogaaicoach.pages.trainingprogram.Duration
import com.yogaaicoach.pages.workout.data.WorkoutService
import com.yogaaicoach.pages.workout.data.models.FocusAreaDataNarrowed
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import kotlin.uuid.Uuid

internal class EditWorkoutViewModel(private val workoutService: WorkoutService) {
    private val viewModelScope = MainScope()

    val isPageLoaded = MutableStateFlow(false)
    val hasChanges = MutableStateFlow(false)

    private val _selectedDuration = MutableStateFlow<Duration?>(null)
    val selectedDuration: StateFlow<Duration?> = _selectedDuration

    private val _selectedFocusAreas = MutableStateFlow<List<FocusAreaDataNarrowed?>>(emptyList())
    val selectedFocusAreas: StateFlow<List<FocusAreaDataNarrowed?>> = _selectedFocusAreas

    private val _allFocusAreas = MutableStateFlow<List<FocusAreaDataNarrowed>>(emptyList())
    val allFocusAreas: StateFlow<List<FocusAreaDataNarrowed>> = _allFocusAreas

    private val _focusAreaErrorText = MutableStateFlow<String?>(null)
    val focusAreaErrorText: StateFlow<String?> = _focusAreaErrorText

    private val _durationErrorText = MutableStateFlow<String?>(null)
    val durationErrorText: StateFlow<String?> = _durationErrorText

    private var isLoading = false
    private var initializedGuid: Uuid? = null

    fun init(workoutGuid: Uuid, defaultDuration: Int) {
        if (workoutGuid == initializedGuid || isLoading) return
        isLoading = true
        initializedGuid = workoutGuid
        updateSelectedDuration(Duration.fromMinutes(defaultDuration), markChanged = false)
        viewModelScope.launch {
            val response = workoutService.getFocusAreas(workoutGuid)
            _selectedFocusAreas.value = response?.selected ?: emptyList()
            _allFocusAreas.value = response?.all ?: emptyList()
            isPageLoaded.value = true
            isLoading = false
        }
    }

    fun updateSelectedDuration(duration: Duration, markChanged: Boolean = true) {
        resizeFocusAreas(duration)
        _selectedDuration.value = duration
        if (markChanged) hasChanges.value = true
    }

    fun selectFocusArea(focusArea: FocusAreaDataNarrowed, index: Int) {
        val list = _selectedFocusAreas.value.toMutableList()
        list[index] = focusArea
        _selectedFocusAreas.value = list
        hasChanges.value = true
    }

    fun saveChanges(workoutGuid: Uuid, onDone: (Uuid, Int) -> Unit) {
        viewModelScope.launch {
            if (!isValid()) return@launch
            val focusAreaGuids = _selectedFocusAreas.value.map { it!!.guid }
            val durationMinutes = _selectedDuration.value!!.minutes
            workoutService.updateWorkout(workoutGuid, durationMinutes, focusAreaGuids)
            onDone(workoutGuid, durationMinutes)
        }
    }

    fun reset() {
        isPageLoaded.value = false
        hasChanges.value = false
        _selectedDuration.value = null
        _selectedFocusAreas.value = emptyList()
        _allFocusAreas.value = emptyList()
        _focusAreaErrorText.value = null
        _durationErrorText.value = null
        isLoading = false
        initializedGuid = null
    }

    private fun resizeFocusAreas(duration: Duration) {
        val targetSize = when (duration) {
            Duration.Minutes15 -> 1
            Duration.Minutes30 -> 2
            Duration.Minutes45 -> 3
            Duration.Minutes60 -> 3
        }
        val current = _selectedFocusAreas.value.toMutableList()
        while (current.size < targetSize) current.add(null)
        while (current.size > targetSize) current.removeLast()
        _selectedFocusAreas.value = current
    }

    private fun isValid(): Boolean {
        _focusAreaErrorText.value = if (_selectedFocusAreas.value.any { it == null }) "Please select all focus areas" else null
        _durationErrorText.value = if (_selectedDuration.value == null) "Please select a duration" else null
        return _focusAreaErrorText.value == null && _durationErrorText.value == null
    }
}
